import { type Blog } from "@/lib/@types/api/blog";
import {defaultLocale} from "@/config";

// 获取博客分类
export const getBlogCls=async (params: Blog.GetBlogClsParams): Promise<Blog.BlogCl[]> => {
    if(!params.lang_code.lang_code)params.lang_code.lang_code=defaultLocale
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/cls_filter`;
    try {
        const res = (await fetch(url, {
            method: "POST",
            body: JSON.stringify({
                ...params,
            }),
            headers: {
                "Content-Type": "application/json"
            },
            next:{ revalidate: 60 }
        }).then((r) => r.json())) as Blog.GetBlogClsResp;
        if (res.code === 200 && res.detail?.cls_list) {
            return res.detail.cls_list;
        } else {
            return [];
        }
    } catch (error) {
        console.error("Error fetching blog categories:", error);
        return [];
    }
};
// 获取博标签
export const getBlogTags=async (params: Blog.GetBlogClsParams): Promise<Blog.BlogTag[]> => {
    if(!params.lang_code.lang_code)params.lang_code.lang_code=defaultLocale
    
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/tag/${params.lang_code.lang_code}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 60 }
    }).then((r) => r.json())) as Blog.GetBlogTagResp;
    if (res.code === 200) {
        return res.detail.tag_list;
    } else {
      return {
        // @ts-ignore
       detail:{
         tag_list:[]
       }

     }
    }
};

// 获取博客列表
export const getBlogList = async (params: Blog.GetBlogListParams): Promise<any> => {
    if(!params.lang_code.lang_code) params.lang_code.lang_code = defaultLocale;
    const locale = params.lang_code.lang_code;
    
    // 统一使用 encodeURIComponent 处理所有 URL 参数
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/blog_filter?lang_code=${encodeURIComponent(locale)}`;
    
    try {
        const requestBody = {
            ...params,
            // 确保 lang_code 结构符合后端期望
            lang_code: { lang_code: locale }
        };
        
        const res = (await fetch(url, {
            method: "POST",
            body: JSON.stringify(requestBody),
            headers: {
                "Content-Type": "application/json; charset=utf-8"
            },
            next:{ revalidate: 600 }
        }).then((r) => r.json())) as Blog.GetBlogListResp;
        
        if (res.code === 200) {
            return res;
        } else {
            console.error("Blog API error:", res.message, { locale, url });
            return { detail: { blog_list: [], blog_filter_count: 0 } };
        }
    } catch (error) {
        console.error("Error fetching blog list:", error);
        return { detail: { blog_list: [], blog_filter_count: 0 } };
    }
};

// 获取博客详情
export const getBlogDetail = async (slug: string, locale: string): Promise<Blog.BlogContent | null> => {
    try {
        // 对 locale 进行 URL 编码，确保多语言支持
        const encodedLocale = encodeURIComponent(locale);
        const url=`${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/blog_slug/${slug}?lang_code=${encodedLocale}`
        const res = (await fetch(url
            ,{
              next:{ revalidate: 1000 }
          }
        ).then((r) => r.json())) as Blog.GetBlogDetailResp;
        if (res.code === 200) {
            return res.detail.blog_content;
        } else {
            console.error("Blog detail API error:", res.message || "Unknown error", { slug, locale });
            return null;
        }
    } catch (e) {
        console.error("Error fetching blog detail:", e, { slug, locale });
        return null;
    }
};


export const getBlogSlug = async (params: {page:number,amount:number}): Promise<Blog.getBlogSlugResp> => {
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/get_blog_slug?page=${params.page}&amount=${params.amount}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.getBlogSlugResp;
    if (res.code === 200) {
        return res
    } else {
        throw new Error(res.message);
    }
};

export  const getBlogComment=async (page:number,limit:number,blog_id:number)=>{
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/comment?page=${page}&limit=${limit}&blog_id=${blog_id}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.GetBlogCommentResp;
    if (res.code === 200) {
        return res
    } else {
        throw new Error(res.message);
    }
}

export const SubmitBlogComment=async (data:Blog.BlogCommentItem)=>{
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/comment`;
    const res = (await fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.BaseResp<any>;
    if (res.code === 200) {
        return res
    } else {
        throw new Error(res.message);
    }
}

export const GetBlogAuthor=async (author_username:string)=>{
    if(!author_username) return
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/user/author?author_username=${author_username}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.BlogAuthorResp;
    if (res.code === 200) {
        return res.detail.author_detail
    } else {
        throw new Error(res.message);
    }
}

// 异步获取博客列表，确保是一个 Promise
export async function fetchBlogList(
	Params: Blog.GetBlogListParams,
): Promise<{ blogList: Blog.BlogListItem[]; blogCount: number }> {
	try {
		const res = await getBlogList(Params);
		return { blogList: res.detail.blog_list, blogCount: res.detail.blog_filter_count };
	} catch (error) {
		console.error("Error fetching blog list:", error);
		return { blogList: [], blogCount: 0 }; // 如果发生错误，返回一个空数组
	}
}
